import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Property } from "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/addLayerItems/CreateLayer";
import LayerSettings from "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/addLayerItems/layerSettings/LayerSettings";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore, Topics } from "shared/common/stores/sidebarStore";
import PopoverElement from "shared/common/utils/popover/PopoverElement";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";

export default function EditLayerSettings() {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const editLayerSettings = defaultStore((state) => state.editLayerSettings);
  const changeEditLayerSettings = defaultStore(
    (state) => state.changeEditLayerSettings
  );

  const changeTopics = sidebarStore((state) => state.changeTopics);
  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeLayerGroups = sidebarStore((state) => state.changeLayerGroups);

  const [canSaveChanges, setCanSaveChanges] = useState<boolean>(false);

  //return all property rows
  const allPropertyRows = () => {
    const rows: Property[] = [];
    rows.push({ name: "ID", datatype: mm.LAYERPROPERTYTYPE.INTEGER });
    if (editLayerSettings) {
      editLayerSettings.propertydef.forEach((property: mm.LayerPropertyDef) => {
        rows.push({ name: property.name, datatype: property.datatype });
      });
    }
    return rows;
  };

  const emptyPointStyle = {
    filter: "",
    symbolizer: "point",
    params: {
      size: 20,
      stroke: [0.05, 0.57, 0.82, 1],
    },
  };

  //check style for point if not add a point style
  const checkStyle = (style: mm.FeatureStyle) => {
    let havePoint = false;
    if (style.rules.length < 3) {
      style.rules.forEach((st) => {
        if (st.symbolizer === "point") {
          havePoint = true;
        }
      });
    } else {
      havePoint = true;
    }
    if (havePoint) {
      return style;
    } else {
      return {
        ...style,
        rules: [...style.rules, emptyPointStyle],
      } as mm.FeatureStyle;
    }
  };

  //remove point object if they removed point from selection
  const removePointObject = (style: mm.FeatureStyle) => {
    style.rules.forEach((st: any, index: number) => {
      // TODO: Type e.g. remove just the any
      if (st.symbolizer === "point") {
        if (!st.params["font-marker"]) {
          style.rules = style.rules.filter(
            (item, stIndex: number) => index !== stIndex
          );
        }
      }
    });

    //check if style can call toJSON function or not
    const hasToJsonMethod = (obj: mm.FeatureStyle) => {
      return typeof obj.toJSON === "function";
    };

    // return style as json
    if (hasToJsonMethod(style)) {
      return JSON.parse(style.toJSON());
    } else {
      return style;
    }
  };

  const propertyRows = useMemo(() => allPropertyRows(), [editLayerSettings]);
  const [groupSelected, setGroupSelected] = useState<string>(
    editLayerSettings?.topic ?? ""
  );
  const [layername, setLayerName] = useState<string>(
    editLayerSettings?.title ?? ""
  );
  const geometryType = useMemo(
    () => editLayerSettings?.geometrytype ?? -1,
    [editLayerSettings]
  );
  const styles: mm.FeatureStyle | undefined = editLayerSettings?.style
    ? checkStyle(editLayerSettings.style)
    : undefined;

  //change layer name
  const changeLayerName = (value: string) => {
    setLayerName(value);
  };

  //change layer group
  const changeGroup = (value: string) => {
    setGroupSelected(value);
    if (editLayerSettings) {
      editLayerSettings.topic = value;
    }
  };

  //check if can save layer changes
  const checkIfCanSave = () => {
    if (layername !== "" && geometryType !== -1) {
      setCanSaveChanges(true);
    } else {
      setCanSaveChanges(false);
    }
  };

  //function that is called to save layer changes
  const onLayersChanged = () => {
    if (viewer === undefined) return;
    const layersArray: Topics[] = [];
    const groups: string[] = [];
    const visibleLayers = viewer
      .getScene()
      .getLayerHandler()
      .layers.filter((layer: mm.Layer) => !layer.options.hideinlayertree);
    visibleLayers.forEach((layer: mm.Layer) => {
      layersArray.push({
        addedFromUser: layer.addedFromUser,
        name: layer.layername,
        title: layer.title,
        folder: false,
        showOnMap: layer.getVisible(),
        icon: layer.options.geometrytype,
        topic: layer.topic,
        layer: layer,
      });
      if (
        !groups.includes(layer.topic) &&
        layer.topic !== "" &&
        layer.addedFromUser
      ) {
        groups.push(layer.topic);
      }
    });
    changeTopics(layersArray);
    changeLayerGroups(groups);
  };

  //return type pf layer
  const getLayerType = (name: string | mm.GEOMETRYTYPE | undefined): number => {
    switch (name) {
      case mm.GEOMETRYTYPE.LINESTRING:
      case "linestring":
      case "LineString":
        return mm.GEOMETRYTYPE.LINESTRING;
      case mm.GEOMETRYTYPE.MULTILINESTRING:
      case "multilinestring":
      case "MultiLineString":
        return mm.GEOMETRYTYPE.MULTILINESTRING;
      case mm.GEOMETRYTYPE.POLYGON:
      case "polygon":
      case "Polygon":
        return mm.GEOMETRYTYPE.POLYGON;
      case mm.GEOMETRYTYPE.MULTIPOLYGON:
      case "multipolygon":
      case "MultiPolygon":
        return mm.GEOMETRYTYPE.MULTIPOLYGON;
      case mm.GEOMETRYTYPE.POINT:
      case "point":
      case "Point":
        return mm.GEOMETRYTYPE.POINT;
      case mm.GEOMETRYTYPE.MULTIPOINT:
      case "multipoint":
      case "MultiPoint":
        return mm.GEOMETRYTYPE.MULTIPOINT;
      default:
        return mm.GEOMETRYTYPE.UNDEFINED;
    }
  };

  //update changes for localstorage layers
  const handleChanges = () => {
    if (styles) {
      const allLayersAddedFromUser = getLayersAddedByUser();
      allLayersAddedFromUser.forEach((item: any) => {
        item.layers.forEach((ls: any) => {
          if (
            editLayerSettings &&
            (ls.title === editLayerSettings.title ||
              ls.layername === editLayerSettings.layername) &&
            getLayerType(ls.geometrytype) === editLayerSettings.geometrytype
          ) {
            const allLayer: mm.Layer[] | undefined = viewer
              ?.getScene()
              .getLayerHandler().layers;
            if (allLayer === undefined) return;
            const layer = allLayer.find(
              (item: mm.Layer) =>
                (item.title === editLayerSettings.title ||
                  item.layername === editLayerSettings.layername) &&
                getLayerType(item.geometrytype) ===
                  editLayerSettings.geometrytype
            );
            if (layer === undefined) return;

            const finalStyle = removePointObject(styles);

            ls.title = layername;
            ls.topic = groupSelected;
            ls.style = finalStyle;

            layer.setOptions({
              title: layername,
              topic: groupSelected,
              style: finalStyle,
            });
            onLayersChanged();
          }
        });
      });
      saveLayersToLocalStorage(
        allLayersAddedFromUser,
        t("alert_success.layer_edited")
      );
    }
    changeEditLayerSettings(undefined);
  };

  //check if can save
  useEffect(() => {
    checkIfCanSave();
  }, [layername, groupSelected, geometryType]);

  return (
    <PopoverElement
      maximizeTable={true}
      visible={true}
      handleClose={() => changeEditLayerSettings(undefined)}
      title={t("edit_layers.title")}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        paddingY={1}
        gap={3}
        height={"100%"}
        sx={{ color: darkTheme ? "white" : "darkblue.main" }}
      >
        <Box
          display={"flex"}
          sx={{ flexDirection: { xs: "column", md: "row" } }}
          gap={2}
        >
          <TextField
            defaultValue={editLayerSettings?.title}
            color="primary"
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
            size="small"
            label={t("create_layer.name")}
            variant="outlined"
            onChange={(event) => changeLayerName(event.target.value)}
          />
          <Autocomplete
            freeSolo
            disabled={false}
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
            size="small"
            options={layerGroups}
            noOptionsText={t("create_layer.group_helper")}
            value={editLayerSettings?.topic ?? ""}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              changeGroup(event.target.value);
              if (
                !layerGroups.includes(event.target.value) &&
                event.target.value !== ""
              ) {
                changeLayerGroups(layerGroups.concat(event.target.value));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("create_layer.group")}
                variant="outlined"
              />
            )}
          />
          <FormControl
            size="small"
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
          >
            <InputLabel>{t("create_layer.geometry_type")}</InputLabel>
            <Select
              disabled
              label="Geometry Type"
              defaultValue={editLayerSettings?.geometrytype}
            >
              <MenuItem value={mm.GEOMETRYTYPE.POINT}>
                {t("create_layer.geometry_type_1")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTIPOINT}>
                {t("create_layer.geometry_type_4")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.LINESTRING}>
                {t("create_layer.geometry_type_2")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTILINESTRING}>
                {t("create_layer.geometry_type_5")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.POLYGON}>
                {t("create_layer.geometry_type_3")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTIPOLYGON}>
                {t("create_layer.geometry_type_6")}
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            disabled={!canSaveChanges}
            sx={{
              borderColor: darkTheme ? "white" : "primary.main",
              color: darkTheme ? "white" : "primary.main",
            }}
            variant="outlined"
            onClick={() =>
              monitor(apm, "save-edit-layer | Layer Settings", handleChanges)
            }
          >
            {t("manage_layers.button_save")}
          </Button>
        </Box>
        <LayerSettings
          editProperties={false}
          infra3dstyles={styles}
          propertyRows={propertyRows}
          setPropertyRows={() => {}}
          checkIfCanSave={() => {}}
          updateExportObject={checkIfCanSave}
        />
      </Box>
    </PopoverElement>
  );
}
